import mapboxgl from 'mapbox-gl';

export function handleZoomToTrackBounds() {
  if (!this.map) return;
  this.pendingTrackBoundsZoom = true;
  this.trackBoundsZoomAttempts = 0;
  this.tryZoomToTrackBounds();
  this.scheduleTrackBoundsZoomRetry();
}

export function handleZoomToTrackItemBounds(e) {
  if (!this.map) return;
  const coordinates = Array.isArray(e?.detail?.coordinates)
    ? e.detail.coordinates
    : [];
  if (!coordinates.length) return;
  const bounds = new mapboxgl.LngLatBounds(coordinates[0], coordinates[0]);
  coordinates.forEach(coord => bounds.extend(coord));
  this.skipNextMoveEndFetch = true;
  this.map.fitBounds(bounds, {
    padding: { top: 80, right: 120, bottom: 80, left: 120 },
    maxZoom: 13,
    duration: 550
  });
}

export function getCurrentBounds() {
  return {
    north: this.map.getBounds().getNorth(),
    east: this.map.getBounds().getEast(),
    south: this.map.getBounds().getSouth(),
    west: this.map.getBounds().getWest()
  };
}

export function isBoundsWithin(inner, outer) {
  if (!inner || !outer) return false;
  return (
    inner.north <= outer.north &&
    inner.east <= outer.east &&
    inner.south >= outer.south &&
    inner.west >= outer.west
  );
}

export function getBoundsArea(bounds) {
  if (!bounds) return 0;
  const width = Math.max(0, Number(bounds.east) - Number(bounds.west));
  const height = Math.max(0, Number(bounds.north) - Number(bounds.south));
  return width * height;
}

export function getBoundsCoverageRatio(inner, outer) {
  const outerArea = this.getBoundsArea(outer);
  if (!outerArea) return 1;
  const innerArea = this.getBoundsArea(inner);
  return innerArea / outerArea;
}

export function getTrackBounds(trackLayer) {
  const actualTrackFeatures = Array.isArray(
    this.latestActualTracksData && this.latestActualTracksData.features
  )
    ? this.latestActualTracksData.features
    : [];
  const trackLayerFeatures = Array.isArray(trackLayer?.features)
    ? trackLayer.features
    : [];
  const tracksMapLayer = Array.isArray(this.props.mapLayers)
    ? this.props.mapLayers.find(layer => layer && layer.name === 'Tracks')
    : null;
  const tracksMapLayerFeatures = Array.isArray(tracksMapLayer?.features)
    ? tracksMapLayer.features
    : [];
  const features =
    actualTrackFeatures.length > 0
      ? actualTrackFeatures
      : trackLayerFeatures.length > 0
      ? trackLayerFeatures
      : tracksMapLayerFeatures;
  if (!features.length) return null;

  const coords = [];
  const pushCoordinates = geometry => {
    if (!geometry || !geometry.type || !geometry.coordinates) return;
    const pushCoord = coord => {
      if (!Array.isArray(coord) || coord.length < 2) return;
      const lng = Number(coord[0]);
      const lat = Number(coord[1]);
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;
      coords.push([lng, lat]);
    };
    if (geometry.type === 'Point') {
      pushCoord(geometry.coordinates);
      return;
    }
    if (geometry.type === 'LineString') {
      geometry.coordinates.forEach(pushCoord);
      return;
    }
    if (geometry.type === 'MultiLineString') {
      geometry.coordinates.forEach(line => line.forEach(pushCoord));
    }
  };

  features.forEach(feature => pushCoordinates(feature && feature.geometry));
  if (!coords.length) return null;

  const bounds = new mapboxgl.LngLatBounds(coords[0], coords[0]);
  coords.forEach(coord => bounds.extend(coord));
  return bounds;
}

export function zoomToTrackWithContext(trackLayer) {
  if (!this.map) return;
  const bounds = this.getTrackBounds(trackLayer);
  if (!bounds) return;

  this.skipNextMoveEndFetch = true;
  this.map.fitBounds(bounds, {
    padding: { top: 80, right: 120, bottom: 80, left: 120 },
    maxZoom: 13,
    duration: 550
  });
}

export function tryZoomToTrackBounds(force) {
  const hasPending = this.pendingTrackBoundsZoom === true;
  if (!force && !hasPending) return false;
  const bounds = this.getTrackBounds(this.props.trackLayer);
  if (!bounds) return false;
  this.pendingTrackBoundsZoom = false;
  this.trackBoundsZoomAttempts = 0;
  if (this.trackBoundsZoomRetryTimer) {
    clearTimeout(this.trackBoundsZoomRetryTimer);
    this.trackBoundsZoomRetryTimer = null;
  }
  this.skipNextMoveEndFetch = true;
  this.map.fitBounds(bounds, {
    padding: { top: 80, right: 120, bottom: 80, left: 120 },
    maxZoom: 13,
    duration: 550
  });
  return true;
}

export function scheduleTrackBoundsZoomRetry() {
  if (!this.pendingTrackBoundsZoom || !this.map) return;
  if (this.trackBoundsZoomRetryTimer) return;
  if (this.trackBoundsZoomAttempts >= 20) {
    this.pendingTrackBoundsZoom = false;
    this.trackBoundsZoomAttempts = 0;
    return;
  }
  this.trackBoundsZoomRetryTimer = setTimeout(() => {
    this.trackBoundsZoomRetryTimer = null;
    this.trackBoundsZoomAttempts += 1;
    const didZoom = this.tryZoomToTrackBounds(true);
    if (!didZoom) {
      this.scheduleTrackBoundsZoomRetry();
    }
  }, 150);
}
